import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Target, CheckCircle2, Store, Loader2, CreditCard, Heart, RotateCcw } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["ASAP (15 min)", "In 30 minutes", "In 1 hour", "Today, 5:00 PM", "Today, 7:00 PM"];

function FavoritesPanel() {
  const { favorites, reorderFavorite, removeFavorite } = useCart();
  if (!favorites.length) return null;
  return (
    <div className="rounded-2xl bg-white border border-line p-4" data-testid="favorites-panel">
      <div className="flex items-center gap-2 mb-3">
        <Heart size={16} className="text-[#8A2231]" />
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-coffee">Your Favorites</span>
      </div>
      <div className="space-y-2.5">
        {favorites.map((f) => (
          <div key={f.id} data-testid="favorite-item" className="flex items-center gap-3 rounded-xl border border-line px-3 py-2.5">
            <div className="flex-1 min-w-0">
              <div className="truncate font-semibold text-sm text-coffee">{f.label}</div>
              <div className="font-mono text-[11px] text-coffee-muted">
                {f.items.reduce((s, i) => s + i.qty, 0)} items · ₹{f.items.reduce((s, i) => s + i.price * i.qty, 0)}
              </div>
            </div>
            <button
              data-testid="reorder-favorite"
              onClick={() => { reorderFavorite(f.id); toast.success("Combo added to your order"); }}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#8A2231] hover:bg-[#6E1926] text-white text-xs font-semibold px-3.5 py-2 transition-colors"
            >
              <RotateCcw size={13} /> Reorder
            </button>
            <button data-testid="delete-favorite" onClick={() => removeFavorite(f.id)} className="text-coffee-muted hover:text-[#8A2231]">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GoalBar({ label, value, goal, unit }) {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  return (
    <div data-testid={`goal-bar-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-coffee-soft">{label}</span>
        <span className="font-mono font-semibold text-coffee">{Math.round(value)}/{goal}{unit} · {pct}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full bg-line overflow-hidden">
        <div className="h-full bg-[#8A2231] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function CartSheet() {
  const { items, remove, setQty, clear, count, subtotal, totalCalories, totalProtein, goals, setGoals, open, setOpen, saveFavorite } = useCart();
  const [step, setStep] = useState("cart");
  const [form, setForm] = useState({ name: "", phone: "", email: "", pickup_time: SLOTS[0] });
  const [loading, setLoading] = useState(false);
  const [orderNo, setOrderNo] = useState("");
  const [favName, setFavName] = useState("");
  const { user } = useAuth();

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
  }, [user]);

  const handleSaveFavorite = () => {
    const fav = saveFavorite(favName);
    if (fav) {
      toast.success("Saved to favorites", { description: fav.label });
      setFavName("");
    }
  };

  const createOrder = async () => {
    const res = await axios.post(`${API}/orders`, {
      items: items.map((i) => ({ name: i.name, price: i.price, qty: i.qty, calories: i.calories || 0, protein: i.protein || 0, addons: i.addons || [] })),
      subtotal,
      ...form,
    });
    return res.data;
  };

  const placeOrder = async () => {
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone");
      return;
    }
    setLoading(true);
    try {
      const order = await createOrder();
      setOrderNo(order.order_number);
      setStep("done");
      clear();
    } catch {
      toast.error("Could not place order. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone");
      return;
    }
    setLoading(true);
    try {
      const order = await createOrder();
      localStorage.setItem("pc_last_order", order.order_number);
      const res = await axios.post(`${API}/payments/checkout`, { order_id: order.id, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start payment. Try again.");
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep("cart"), 300);
  };

  return (
    <Sheet open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-beige border-l border-line p-0 flex flex-col" data-testid="cart-sheet">
        <SheetHeader className="px-6 py-5 border-b border-line">
          <SheetTitle className="font-display text-2xl font-bold text-coffee flex items-center gap-2">
            <ShoppingBag size={22} /> {step === "done" ? "Order Confirmed" : step === "checkout" ? "Store Pickup" : "Your Order"}
          </SheetTitle>
        </SheetHeader>

        {step === "done" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="order-confirmation">
            <CheckCircle2 size={64} className="text-[#8A2231]" />
            <h3 className="mt-5 font-display text-3xl font-bold text-coffee">You&apos;re fueled up!</h3>
            <p className="mt-2 text-coffee-soft">Order <span className="font-mono font-bold text-coffee" data-testid="order-number">{orderNo}</span> is confirmed.</p>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white border border-line px-4 py-2 text-sm text-coffee">
              <Store size={16} className="text-[#8A2231]" /> Pick up at The Fuel Lounge™
            </div>
            {form.email ? <p className="mt-3 text-sm text-coffee-soft">A receipt is on its way to {form.email}.</p> : null}
            <a data-testid="track-order-link" href={`/track?order=${orderNo}`}
              className="mt-8 rounded-full bg-[#8A2231] hover:bg-[#6E1926] text-white font-semibold px-8 py-3 transition-colors">
              Track Order
            </a>
            <button data-testid="cart-continue" onClick={close} className="mt-3 text-coffee-soft underline underline-offset-4">
              Continue browsing
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex-1 overflow-y-auto px-6 py-8" data-testid="cart-empty">
            <div className="flex flex-col items-center text-center">
              <ShoppingBag size={48} className="text-coffee-muted" />
              <p className="mt-4 font-display text-xl text-coffee">Your order is empty</p>
              <p className="mt-1 text-coffee-soft text-sm">Add a shake or meal to get started.</p>
              <button onClick={close} className="mt-6 rounded-full border border-line px-6 py-2.5 font-semibold text-coffee hover:border-teal-brand transition-colors">Browse Menu</button>
            </div>
            <div className="mt-8"><FavoritesPanel /></div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Macro goal tracker */}
              <div className="rounded-2xl bg-white border border-line p-4" data-testid="macro-goal-tracker">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={16} className="text-[#8A2231]" />
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-coffee">Daily Macro Goal</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <label className="text-xs text-coffee-soft">Calories
                    <input data-testid="goal-calories" type="number" value={goals.calories} onChange={(e) => setGoals({ ...goals, calories: Number(e.target.value) })}
                      className="mt-1 w-full rounded-lg border border-line bg-beige px-3 py-2 font-mono text-coffee outline-none focus:border-teal-brand" />
                  </label>
                  <label className="text-xs text-coffee-soft">Protein (g)
                    <input data-testid="goal-protein" type="number" value={goals.protein} onChange={(e) => setGoals({ ...goals, protein: Number(e.target.value) })}
                      className="mt-1 w-full rounded-lg border border-line bg-beige px-3 py-2 font-mono text-coffee outline-none focus:border-teal-brand" />
                  </label>
                </div>
                <div className="space-y-2.5">
                  <GoalBar label="Calories" value={totalCalories} goal={goals.calories} unit="" />
                  <GoalBar label="Protein" value={totalProtein} goal={goals.protein} unit="g" />
                </div>
              </div>

              {items.map((i) => (
                <div key={i.key} data-testid="cart-line-item" className="flex items-center gap-3 border-b border-line pb-4">
                  <div className="flex-1">
                    <div className="font-semibold text-coffee">{i.name}</div>
                    <div className="font-mono text-xs text-coffee-muted">{i.calories || 0} kcal · {i.protein || 0}g protein{i.addons?.length ? ` · +${i.addons.length} add-on` : ""}</div>
                    <div className="mt-2 inline-flex items-center rounded-full border border-line">
                      <button data-testid="qty-dec" onClick={() => setQty(i.key, i.qty - 1)} className="px-2 py-1 text-coffee-soft"><Minus size={14} /></button>
                      <span className="px-3 font-mono text-sm text-coffee">{i.qty}</span>
                      <button data-testid="qty-inc" onClick={() => setQty(i.key, i.qty + 1)} className="px-2 py-1 text-coffee-soft"><Plus size={14} /></button>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-coffee">₹{i.price * i.qty}</div>
                    <button data-testid="cart-remove" onClick={() => remove(i.key)} className="mt-2 text-coffee-muted hover:text-[#8A2231]"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}

              {/* Save current order as a favorite combo */}
              <div className="rounded-2xl border border-dashed border-line p-3 flex items-center gap-2" data-testid="save-favorite-row">
                <input
                  data-testid="favorite-name"
                  value={favName}
                  onChange={(e) => setFavName(e.target.value)}
                  placeholder="Name this combo (optional)"
                  className="flex-1 min-w-0 rounded-lg border border-line bg-beige px-3 py-2 text-sm text-coffee outline-none focus:border-teal-brand"
                />
                <button
                  data-testid="save-favorite"
                  onClick={handleSaveFavorite}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-2 text-sm font-semibold text-coffee hover:border-teal-brand transition-colors"
                >
                  <Heart size={14} /> Save
                </button>
              </div>

              <FavoritesPanel />

              {step === "checkout" && (
                <div className="rounded-2xl bg-white border border-line p-4 space-y-3" data-testid="checkout-form">
                  <input data-testid="checkout-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full rounded-lg border border-line bg-beige px-3 py-2.5 text-coffee outline-none focus:border-teal-brand" />
                  <input data-testid="checkout-phone" placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full rounded-lg border border-line bg-beige px-3 py-2.5 text-coffee outline-none focus:border-teal-brand" />
                  <input data-testid="checkout-email" type="email" placeholder="Email (for your receipt)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full rounded-lg border border-line bg-beige px-3 py-2.5 text-coffee outline-none focus:border-teal-brand" />
                  <select data-testid="checkout-time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
                    className="w-full rounded-lg border border-line bg-beige px-3 py-2.5 text-coffee outline-none focus:border-teal-brand">
                    {SLOTS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div className="border-t border-line px-6 py-5 bg-white">
              <div className="flex items-center justify-between mb-4">
                <span className="text-coffee-soft">Subtotal ({count} items)</span>
                <span className="font-mono text-2xl font-bold text-coffee" data-testid="cart-subtotal">₹{subtotal}</span>
              </div>
              {step === "checkout" ? (
                <div className="space-y-2.5">
                  <button data-testid="pay-now" onClick={payNow} disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8A2231] hover:bg-[#6E1926] disabled:opacity-60 text-white font-semibold py-3.5 transition-colors">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} Pay in Advance · Card
                  </button>
                  <button data-testid="place-order" onClick={placeOrder} disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-line text-coffee hover:border-teal-brand disabled:opacity-60 font-semibold py-3 transition-colors">
                    <Store size={18} /> Pay at Pickup
                  </button>
                  <p className="text-center text-xs text-coffee-muted">UPI activates with a live India account. Test card: 4242 4242 4242 4242</p>
                </div>
              ) : (
                <button data-testid="go-checkout" onClick={() => setStep("checkout")}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8A2231] hover:bg-[#6E1926] text-white font-semibold py-3.5 transition-colors">
                  <Store size={18} /> Checkout · Store Pickup
                </button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
